import React from 'react';

/**
 * PageHeader — the canonical landing-page hero block.
 *
 * Every primary page in the platform — Overview, Agent Lifecycle,
 * Workflow Canvas, Trust Center, Compliance Center, Workflow
 * Intelligence, Team Management, Admin Center, etc. — should render
 * its title through this component so users get a consistent visual
 * anchor at the top of every screen. Icon-square + title + subtitle +
 * optional badge + optional right-side actions, all in brand-aligned
 * type and colour.
 *
 * Why this lives in /components/common rather than next to a single
 * page: the platform had drifted into ~four different header
 * implementations (inline 26px on Overview, 20px GovernanceDesignSystem
 * for governance pages, no-icon 18px on Approval Center, ad-hoc on
 * Workflow Intelligence). The screenshots a security/compliance buyer
 * sees should read as ONE product, not four — uniform headers are the
 * cheapest fix for that.
 *
 * Design tokens kept inline (not imported from a theme module) so this
 * component is fully self-contained and the brand palette stays
 * grep-able from a single file.
 *
 * @param {Object} props
 * @param {React.ComponentType} [props.icon] — lucide icon component (preferred)
 * @param {string} [props.iconEmoji] — alternative if no lucide icon fits
 * @param {string} props.title — the page title (required)
 * @param {string} [props.subtitle] — single-line description below the title
 * @param {string} [props.badge] — small uppercase pill next to the title (e.g. "BYOA", "Lets")
 * @param {string} [props.badgeColor] — accent for the badge; defaults to brand red
 * @param {React.ReactNode} [props.actions] — right-side button group (Refresh, Export, +Add)
 * @param {string} [props.accent] — override the icon-square accent (e.g. for ExteLets pages
 *                                   you might pass the deep indigo)
 * @param {string} [props.accentBg] — override the icon-square background tint
 * @param {string} [props.accentBorder] — override the icon-square border tone
 */
const TOKENS = {
  ink:          '#1C1917',  // brand charcoal — title text
  muted:        '#78716C',  // brand stone — subtitle text
  border:       '#E7E5E4',  // hairline divider beneath every header
  brand:        '#E24A3A',  // primary — default accent for icon square
  brandLight:   '#FEF2F0',  // tinted background inside icon square
  brandBorder:  '#F5C4BE',  // soft border around icon square
};

export default function PageHeader({
  icon: Icon,
  iconEmoji,
  title,
  subtitle,
  badge,
  badgeColor,
  actions,
  accent       = TOKENS.brand,
  accentBg     = TOKENS.brandLight,
  accentBorder = TOKENS.brandBorder,
}) {
  // Resolve badge styling — defaults to the same accent as the icon
  // square so a page that wants a custom accent (e.g. SysLets pages
  // using warm graphite) gets a consistent badge tone for free.
  const badgeAccent     = badgeColor || accent;
  const badgeBackground = accentBg;
  const badgeBorder     = accentBorder;

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 16,
      paddingBottom: 20,
      marginBottom: 24,
      borderBottom: `1px solid ${TOKENS.border}`,
    }}>
      {/* Left column — icon + title block + subtitle. */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        minWidth: 0,
        flex: 1,
      }}>
        {(Icon || iconEmoji) && (
          <div
            aria-hidden="true"
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: `linear-gradient(135deg, ${accentBg} 0%, #FFFFFF 100%)`,
              border: `1px solid ${accentBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            }}
          >
            {Icon
              ? <Icon size={20} color={accent} strokeWidth={2} />
              : <span style={{ fontSize: 20, lineHeight: 1 }}>{iconEmoji}</span>}
          </div>
        )}

        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}>
            <h1 style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 700,
              color: TOKENS.ink,
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
              wordBreak: 'break-word',
            }}>
              {title}
            </h1>
            {badge && (
              <span style={{
                fontSize: 10,
                fontWeight: 700,
                color: badgeAccent,
                background: badgeBackground,
                border: `1px solid ${badgeBorder}`,
                padding: '3px 9px',
                borderRadius: 6,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                whiteSpace: 'nowrap',
              }}>{badge}</span>
            )}
          </div>
          {subtitle && (
            <p style={{
              margin: '5px 0 0 0',
              fontSize: 13,
              color: TOKENS.muted,
              lineHeight: 1.5,
            }}>
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Right column — optional action buttons (Refresh, Export, Add, etc.). */}
      {actions && (
        <div style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexShrink: 0,
        }}>
          {actions}
        </div>
      )}
    </div>
  );
}
